import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  getIllustrationMappingsData,
  createIllustrationMapping,
  updateIllustrationMapping,
  deleteIllustrationMapping,
  getIllustrationMappingInfo,
} from "../api/illustration-mappings"
import { formatISODate } from "../utils/datetime"

// The global error handler sends `message`; the mappings controllers send `msg`
// (e.g. duplicate mapping, not found).
const errorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.message ||
  fallback

export const useIllustrationMappingsData = ({
  endpoint,
  page,
  limit,
  sortBy,
  filters,
}) =>
  useQuery({
    queryKey: [endpoint, page, limit, sortBy, filters],
    queryFn: () =>
      getIllustrationMappingsData({ endpoint, page, limit, sortBy, filters }),
    select: (data) => ({
      ...data,
      data: data?.data?.map(toMapping) || [],
    }),
    retry: false,
  })

const toMapping = (mappingDto) => ({
  ...mappingDto,
  createdBy: mappingDto?.Creator?.username || "N/A",
  lastModifiedBy: mappingDto?.Updater?.username || "N/A",
  createdAt: formatISODate(mappingDto?.CreatedAt) || "",
})

export const useCreateIllustrationMappings = ({
  endpoint,
  page,
  limit,
  sortBy,
  filters,
}) => {
  const queryClient = useQueryClient()
  const queryKey = [endpoint, page, limit, sortBy, filters]
  return useMutation({
    mutationFn: (payload) => createIllustrationMapping({ endpoint, payload }),
    onError: (error) =>
      toast.error(errorMessage(error, "Failed to create mapping")),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useUpdateIllustrationMappings = ({
  endpoint,
  illustrationMappingId,
  page,
  limit,
  sortBy,
  filters,
}) => {
  const queryClient = useQueryClient()
  const queryKey = [endpoint, page, limit, sortBy, filters]
  const singleMappingKey = [endpoint, "info", illustrationMappingId]
  return useMutation({
    mutationFn: (payload) => updateIllustrationMapping({ endpoint, payload }),
    onError: (error) =>
      toast.error(errorMessage(error, "Failed to update mapping")),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: singleMappingKey })
    },
  })
}

export const useDeleteIllustrationMappings = ({
  endpoint,
  page,
  limit,
  sortBy,
  filters,
}) => {
  const queryClient = useQueryClient()
  const queryKey = [endpoint, page, limit, sortBy, filters]
  return useMutation({
    mutationFn: (mappingId) =>
      deleteIllustrationMapping({ endpoint, mappingId }),
    onError: (error) =>
      toast.error(errorMessage(error, "Failed to delete mapping")),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  })
}

export const useIllustrationMappingInfo = ({ endpoint, mappingId }) =>
  useQuery({
    queryKey: [endpoint, "info", mappingId],
    queryFn: () => getIllustrationMappingInfo({ endpoint, mappingId }),
    enabled: !!mappingId,
  })
